import type { Questionnaire } from '@medplum/fhirtypes'
import { featureFlags } from '@seen/core/featureFlags'
import type { Participant } from '@seen/core/models/patients/participant'
import * as questionnaires from '@seen/core/models/questionnaires/index'
import { Role } from '@seen/core/roles'
import { cn, Tabs, TabsContent, TabsList, TabsTrigger } from '@seen/ui'
import {
  type ComponentPropsWithoutRef,
  type CSSProperties,
  type JSX,
  type ReactNode,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ScrollArea } from '#src/components/core/scroll-area/ScrollArea.tsx'
import { ParticipantLabelsCard } from '#src/components/ParticipantLabelsCard.tsx'
import { AllergiesCard } from '#src/components/patient/detail-content/AllergiesCard.tsx'
import { ConditionsCard } from '#src/components/patient/detail-content/ConditionsCard.tsx'
import { ContactTab } from '#src/components/patient/detail-content/contact-tab/ContactTab.tsx'
import { DietCard } from '#src/components/patient/detail-content/DietCard.tsx'
import {
  FacesheetCard,
  FacesheetCardCollapseTrigger,
  FacesheetCardContent,
  FacesheetCardHeader,
  FacesheetCardLabel,
  FacesheetCardTitle,
  type FacesheetCardName,
  type FacesheetTab,
  FacesheetTabContext,
  type FacesheetTabContextValue,
  isFacesheetTab,
  reportFacesheetCardsToggledAll,
  reportFacesheetTabSelected,
} from '#src/components/patient/detail-content/facesheet/index.ts'
import { MedicationsCard } from '#src/components/patient/detail-content/MedicationsCard.tsx'
import { MemoTab } from '#src/components/patient/detail-content/memo-tab/index.ts'
import { MyNotepadCard } from '#src/components/patient/detail-content/MyNotepadCard.tsx'
import { NotepadCard } from '#src/components/patient/detail-content/NotepadCard.tsx'
import { SsnField } from '#src/components/patient/detail-content/SsnField.tsx'
import { QuestionnaireForm } from '#src/components/questionnaire-form/QuestionnaireForm.tsx'
import { RelevantParticipantObservations } from '#src/components/RelevantParticipantObservations.tsx'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useCurrentPractitionerPerms } from '#src/hooks/useCurrentPractitionerPerms.ts'
import { useElementSize } from '#src/hooks/useElementSize.ts'
import { useFacesheetCardHotkeys } from '#src/hooks/useFacesheetCardHotkeys.ts'
import { useFacesheetHeaderCollapse } from '#src/hooks/useFacesheetHeaderCollapse.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { usePortalStore } from '#src/hooks/usePortalStore.ts'

export interface PatientDetailContentProps extends ComponentPropsWithoutRef<'div'> {
  participant: Participant
  scrollRestorationIdPrefix?: string | null
  // Rendered inside the sticky header container above the tab bar.
  header?: ReactNode
}

const questionnaireCards = [
  {
    questionnaire: questionnaires.SocialHistoryQuestionnaire,
    rumName: 'social_history',
  },
] as const satisfies {
  questionnaire: Questionnaire
  rumName: FacesheetCardName
}[]

// The free-text "Day Center Attendance" item is replaced in the facesheet by the
// structured day center schedule row in the header card, so hide it from the
// questionnaire.
const SERVICE_ALLOCATION_EXCLUDED_LINK_IDS = ['day-center-attendance']

// SSN renders through SsnField instead, which masks it; the header note
// renders through NotepadCard at the top of Highlights.
const ADDITIONAL_INFO_EXCLUDED_LINK_IDS = ['ssn', 'header-note']

// Gap between the header container's border/shadow and the top of a card, at
// rest and while a section title is pinned (Figma 346-16938).
const CARD_GAP_PX = 16

// The facesheet's plain questionnaire sections: a card whose whole body is one
// QuestionnaireForm. `children` render after the form, for the rare section
// with an extra field.
function QuestionnaireCard({
  questionnaire,
  participant,
  rumName,
  excludeLinkIds,
  children,
}: {
  questionnaire: Questionnaire
  participant: Participant
  rumName: FacesheetCardName
  excludeLinkIds?: string[]
  children?: ReactNode
}): JSX.Element {
  return (
    <FacesheetCard rumName={rumName}>
      <FacesheetCardHeader>
        <FacesheetCardTitle>
          <FacesheetCardLabel>{questionnaire.title}</FacesheetCardLabel>
          <FacesheetCardCollapseTrigger />
        </FacesheetCardTitle>
      </FacesheetCardHeader>
      <FacesheetCardContent>
        <QuestionnaireForm
          questionnaire={questionnaire}
          participant={participant}
          excludeLinkIds={excludeLinkIds}
        />
        {children}
      </FacesheetCardContent>
    </FacesheetCard>
  )
}

export function PatientDetailContent({
  participant,
  className,
  scrollRestorationIdPrefix,
  header,
  ...delegated
}: PatientDetailContentProps): JSX.Element {
  const [headerContainerRef, { height: headerContainerHeight }] =
    useElementSize<HTMLDivElement>()
  // Gio's scroll-collapse (Figma 665-2804). Only the active tab's scroller is
  // mounted, so this holds whichever one is live; the hook writes
  // `data-collapsed` on the header container and the collapse itself runs in
  // CSS from there.
  const [scroller, setScroller] = useState<HTMLDivElement | null>(null)
  useFacesheetHeaderCollapse(scroller, headerContainerRef)
  const facesheetImprovements = useFeatureFlag(
    featureFlags.facesheetImprovements
  )
  // Read by the hotkey report; the tabs themselves stay uncontrolled.
  const activeTabRef = useRef<FacesheetTab>('highlights')
  useFacesheetCardHotkeys((state) => {
    if (facesheetImprovements) {
      reportFacesheetCardsToggledAll({
        participantId: participant.id,
        tab: activeTabRef.current,
        state,
      })
    }
  })
  const showMemo = usePortalStore((state) => state.showMemo)
  const { isAuditor } = useCurrentPractitionerPerms()
  const showMemoTab = showMemo && !isAuditor
  const showForYouTab = facesheetImprovements
  const showLabelsCard = !facesheetImprovements
  const isPcp = useCurrentPractitioner().isRole(Role.PCP)

  // The collapse animates the header container's height, so useElementSize
  // re-measures (and re-renders this component) on every frame of the
  // transition. Memoizing keeps the cards from re-rendering with it — only the
  // padding that tracks the header height needs to move.
  const highlightsCards = useMemo(
    () => (
      <>
        <RelevantParticipantObservations
          participantReferenceString={participant.reference.reference}
          variant="highlights"
        />
        {showLabelsCard && (
          <ParticipantLabelsCard
            participantReferenceString={participant.reference.reference}
          />
        )}
        {/* <PatientObservationsChart participant={participant} /> */}
        <ConditionsCard participant={participant} />
        <MedicationsCard participant={participant} />
        <AllergiesCard participant={participant} />
        <DietCard participant={participant} />
        {questionnaireCards.map(({ questionnaire, rumName }) => (
          <QuestionnaireCard
            key={questionnaire.name}
            questionnaire={questionnaire}
            participant={participant}
            rumName={rumName}
          />
        ))}
        <QuestionnaireCard
          questionnaire={questionnaires.ServiceAllocationQuestionnaire}
          participant={participant}
          rumName="service_allocation"
          excludeLinkIds={SERVICE_ALLOCATION_EXCLUDED_LINK_IDS}
        />
        <QuestionnaireCard
          questionnaire={questionnaires.AdditionalInformationQuestionnaire}
          participant={participant}
          rumName="additional_information"
          excludeLinkIds={ADDITIONAL_INFO_EXCLUDED_LINK_IDS}
        >
          <SsnField participant={participant} />
        </QuestionnaireCard>
      </>
    ),
    [participant, showLabelsCard]
  )

  // Persistent-tabs layout: the header card and tab bar sit in a container
  // pinned to the top of the panel, and each tab's scroll area fills the
  // whole panel so its content scrolls behind the container (Figma node
  // 191-1515). The container's blur/shadow/veil only exists while content
  // is actually behind it: a scroll-driven animation (the
  // facesheet-header-scrolled keyframes in @seen/ui baseTailwindConfig)
  // fades it in over the first 24px of scroll, reading the active tab's
  // scroller via a named scroll timeline scoped on the wrapper below.
  // Where scroll-driven animations are unsupported (jsdom), the 0s
  // animation completes immediately and fill-mode keeps the end state:
  // the veil is simply always on. The measured container height pads the
  // content down past it. Flags and the external-system links live in the
  // header; the pinned note lives in the Highlights tab.
  const scrollRestorationId = (tab: string): string | null =>
    scrollRestorationIdPrefix
      ? `${scrollRestorationIdPrefix}-facesheet-${tab}-${participant.id}`
      : null
  const tabContexts = useMemo(
    (): Record<Exclude<FacesheetTab, 'memo'>, FacesheetTabContextValue> => ({
      'for-you': { participantId: participant.id, tab: 'for-you' },
      highlights: { participantId: participant.id, tab: 'highlights' },
      contact: { participantId: participant.id, tab: 'contact' },
    }),
    [participant.id]
  )
  const handleTabChange = (tab: string): void => {
    if (!isFacesheetTab(tab)) {
      return
    }
    activeTabRef.current = tab
    if (showForYouTab) {
      reportFacesheetTabSelected({ participantId: participant.id, tab })
    }
  }
  const cardTop = headerContainerHeight + CARD_GAP_PX
  const tabContentStyle = { paddingTop: cardTop }
  return (
    <div
      className={cn(
        'relative flex min-h-0 flex-col',
        '[timeline-scope:--facesheet-header-scroll]',
        className
      )}
      // Sticky section titles (FacesheetCardHeader) read these to pin below
      // the absolutely-positioned header container instead of sliding
      // underneath it at the scroll viewport's top edge, and to cover the
      // gap they leave above themselves while pinned.
      style={
        {
          '--facesheet-sticky-top': `${cardTop}px`,
          '--facesheet-card-gap': `${CARD_GAP_PX}px`,
        } as CSSProperties
      }
      {...delegated}
    >
      <Tabs
        defaultValue="highlights"
        onValueChange={handleTabChange}
        className="flex min-h-0 flex-1 flex-col"
      >
        <div
          ref={headerContainerRef}
          className={cn(
            'absolute inset-x-0 top-0 z-10 animate-facesheet-header-scrolled [animation-range:0px_24px] [animation-timeline:--facesheet-header-scroll]',
            // The header card reads the collapse state off this container.
            'group/facesheet-header'
          )}
        >
          <div className="flex flex-col gap-2.5 px-4 pt-4 pb-2">
            {header}
            <TabsList className="flex h-7 w-full gap-0 rounded-md border bg-muted p-[3px] shadow-none dark:border-zinc-700">
              {showForYouTab && (
                <TabsTrigger value="for-you" className={TAB_TRIGGER_CLASS}>
                  For you
                </TabsTrigger>
              )}
              <TabsTrigger value="highlights" className={TAB_TRIGGER_CLASS}>
                Highlights
              </TabsTrigger>
              <TabsTrigger value="contact" className={TAB_TRIGGER_CLASS}>
                Contact
              </TabsTrigger>
              {showMemoTab && (
                <TabsTrigger value="memo" className={TAB_TRIGGER_CLASS}>
                  Memo
                </TabsTrigger>
              )}
            </TabsList>
          </div>
        </div>
        <TabsContent value="highlights" className="min-h-0 flex-1">
          <ScrollArea
            scrollRestorationId={scrollRestorationId('highlights')}
            scrollElementRef={setScroller}
            contentClassName={FACESHEET_SCROLL_TIMELINE_CLASS}
          >
            <div
              className="flex flex-col gap-4 px-4 pb-4"
              style={tabContentStyle}
            >
              <FacesheetTabContext.Provider value={tabContexts.highlights}>
                <NotepadCard participant={participant} />
                {highlightsCards}
              </FacesheetTabContext.Provider>
            </div>
          </ScrollArea>
        </TabsContent>
        {showForYouTab && (
          <TabsContent value="for-you" className="min-h-0 flex-1">
            <ScrollArea
              scrollRestorationId={scrollRestorationId('for-you')}
              scrollElementRef={setScroller}
              contentClassName={FACESHEET_SCROLL_TIMELINE_CLASS}
            >
              <div
                className="flex flex-col gap-4 px-4 pb-4"
                style={tabContentStyle}
              >
                <FacesheetTabContext.Provider value={tabContexts['for-you']}>
                  <MyNotepadCard participant={participant} />
                  <RelevantParticipantObservations
                    participantReferenceString={participant.reference.reference}
                  />
                  {isPcp && (
                    <>
                      <ConditionsCard participant={participant} />
                      <MedicationsCard participant={participant} />
                      <AllergiesCard participant={participant} />
                      <DietCard participant={participant} variant="pcp" />
                    </>
                  )}
                </FacesheetTabContext.Provider>
              </div>
            </ScrollArea>
          </TabsContent>
        )}
        <TabsContent value="contact" className="min-h-0 flex-1">
          <ScrollArea
            scrollRestorationId={scrollRestorationId('contact')}
            scrollElementRef={setScroller}
            contentClassName={FACESHEET_SCROLL_TIMELINE_CLASS}
          >
            <div className="px-4 pb-4" style={tabContentStyle}>
              <FacesheetTabContext.Provider value={tabContexts.contact}>
                <ContactTab participant={participant} />
              </FacesheetTabContext.Provider>
            </div>
          </ScrollArea>
        </TabsContent>
        {showMemoTab && (
          <TabsContent value="memo" className="min-h-0 flex-1">
            <ScrollArea
              scrollRestorationId={scrollRestorationId('memo')}
              scrollElementRef={setScroller}
              contentClassName={FACESHEET_SCROLL_TIMELINE_CLASS}
            >
              <div className="px-4 pb-4" style={tabContentStyle}>
                <MemoTab participant={participant} />
              </div>
            </ScrollArea>
          </TabsContent>
        )}
      </Tabs>
    </div>
  )
}

// Each tab's scroller publishes the timeline that drives the sticky header's
// scroll-gated veil. Only the active tab is mounted, so exactly one element
// declares the name at a time.
const FACESHEET_SCROLL_TIMELINE_CLASS =
  '[scroll-timeline:--facesheet-header-scroll_y]'

// Figma TabMenu button metrics; active tab is a white pill with a soft drop
// shadow inside the cream segmented control.
const TAB_TRIGGER_CLASS =
  'h-[22px] flex-1 rounded-[5px] px-1.5 py-1 font-normal text-foreground-secondary text-xs tracking-[-0.01em] data-[state=active]:text-foreground data-[state=active]:shadow-[0px_2px_2px_rgba(0,0,0,0.09)] dark:data-[state=active]:shadow-[0px_2px_2px_rgba(255,255,255,0.07)]'
